import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
} from 'recharts'
import type { ForecastState } from '../../types'
import type { ChartPoint } from '../../types'
import type { UserStatus } from '@nextweight/shared'
import { useTranslation } from '../../lib/i18n'

interface ChartDataPoint {
  date: number
  actual?: number
  forecast?: number
  forecastMin?: number
  forecastMax?: number
  plateau?: number
}

function buildChartData(
  chart: ChartPoint[],
  state: ForecastState,
  today: number,
): ChartDataPoint[] {
  const points: ChartDataPoint[] = chart.map((p) => ({
    date: p.date,
    actual: p.weight_smoothed,
  }))

  const last = chart[chart.length - 1]
  if (!last) return points

  const lastSmoothed = last.weight_smoothed

  if (state.status === 'ACTIVE' && state.forecast.length > 0) {
    // Соединительная точка — коридор начинается с нуля (min=max=текущий вес)
    points.push({
      date: today,
      actual: lastSmoothed,
      forecast: lastSmoothed,
      forecastMin: lastSmoothed,
      forecastMax: lastSmoothed,
    })

    for (const point of state.forecast) {
      points.push({
        date: today + point.days * 86400,
        forecast: point.value,
        forecastMin: point.min,
        forecastMax: point.max,
      })
    }
  } else if (state.status === 'PLATEAU') {
    points.push({ date: today, actual: lastSmoothed, plateau: lastSmoothed })
    // Extend plateau line to 30 days out
    points.push({ date: today + 30 * 86400, plateau: lastSmoothed })
  }

  return points
}

function getChartColors(state: ForecastState) {
  const isLow = state.current.accuracy === 'LOW'
  return {
    actualColor: isLow ? '#9CA3AF' : '#111827',
    forecastColor: isLow ? '#9CA3AF' : '#3B82F6',
    corridorColor: isLow ? '#9CA3AF' : '#3B82F6',
  }
}

function getYAxisTicks(data: ChartDataPoint[]): number[] {
  const values = data.flatMap((p) =>
    [p.actual, p.forecast, p.forecastMin, p.forecastMax, p.plateau].filter((v): v is number => v !== undefined)
  )
  if (values.length === 0) return []
  const min = Math.floor(Math.min(...values)) - 1
  const max = Math.ceil(Math.max(...values)) + 1
  return Array.from({ length: max - min + 1 }, (_, i) => min + i)
}

function getXAxisTicks(status: UserStatus, data: ChartDataPoint[]): number[] {
  if (data.length === 0) return []
  const first = data[0]!.date
  const last = data[data.length - 1]!.date
  const today = Date.now() / 1000

  if (status === 'ACTIVE') {
    const horizons = [30, 90, 180, 365]
    const ticks: number[] = [today]
    for (const h of horizons) {
      const ts = today + h * 86400
      if (ts <= last + 86400) ticks.push(ts)
    }
    return ticks
  }

  if (status === 'PLATEAU') {
    return [first, today]
  }

  // WARMUP
  return [first, today]
}

function formatXAxisDate(
  ts: number,
  today: number,
  t: (key: string) => string,
  lang: string,
): string {
  const DAY = 86400
  if (Math.abs(ts - today) < DAY / 2) return t('chart.today')
  if (Math.abs(ts - (today + 30 * DAY)) < DAY / 2) return t('chart.1m')
  if (Math.abs(ts - (today + 90 * DAY)) < DAY / 2) return t('chart.3m')
  if (Math.abs(ts - (today + 180 * DAY)) < DAY / 2) return t('chart.6m')
  if (Math.abs(ts - (today + 365 * DAY)) < DAY / 2) return t('chart.1y')

  const d = new Date(ts * 1000)
  const month = d.toLocaleString(lang, { month: 'short' }).replace('.', '')
  return `${d.getDate()} ${month}`
}

interface WeightChartProps {
  state: ForecastState
  chart: ChartPoint[]
}

export function WeightChart({ state, chart }: WeightChartProps) {
  const { t, lang } = useTranslation()
  const today = Date.now() / 1000
  const chartData = buildChartData(chart, state, today)
  const { actualColor, forecastColor, corridorColor } = getChartColors(state)
  const ticks = getXAxisTicks(state.status, chartData)
  const yTicks = getYAxisTicks(chartData)
  const showDots = state.status === 'WARMUP'

  return (
    <ResponsiveContainer width="100%" height={240}>
      <ComposedChart data={chartData} margin={{ top: 8, right: 8, bottom: 0, left: 0 }}>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke="var(--tg-theme-secondary-bg-color, #e5e7eb)"
          vertical={true}
          horizontal={true}
        />
        <XAxis
          dataKey="date"
          type="number"
          scale="time"
          domain={['dataMin', 'dataMax']}
          ticks={ticks}
          tickFormatter={(ts: number) => formatXAxisDate(ts, today, t, lang)}
          tick={{ fontSize: 11, fill: 'var(--tg-theme-hint-color, #888)' }}
          axisLine={false}
          tickLine={false}
        />
        <YAxis
          domain={yTicks.length > 0 ? [yTicks[0]!, yTicks[yTicks.length - 1]!] : ['auto', 'auto']}
          ticks={yTicks}
          tickFormatter={(v: number) => `${v}`}
          width={40}
          tick={{ fontSize: 11, fill: 'var(--tg-theme-hint-color, #888)' }}
          axisLine={false}
          tickLine={false}
        />

        {/* Corridor */}
        {state.status === 'ACTIVE' && (
          <>
            <Area
              dataKey="forecastMax"
              stroke="none"
              fill={corridorColor}
              fillOpacity={0.2}
              connectNulls={false}
              isAnimationActive={false}
            />
            <Area
              dataKey="forecastMin"
              stroke="none"
              fill="white"
              fillOpacity={1}
              connectNulls={false}
              isAnimationActive={false}
            />
          </>
        )}

        {/* Actual line */}
        <Line
          dataKey="actual"
          stroke={actualColor}
          strokeWidth={2}
          dot={showDots ? { r: 3, fill: actualColor } : false}
          connectNulls={false}
          isAnimationActive={false}
        />

        {/* Forecast line */}
        {state.status === 'ACTIVE' && (
          <Line
            dataKey="forecast"
            stroke={forecastColor}
            strokeDasharray="5 5"
            strokeWidth={2}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}

        {/* Plateau line */}
        {state.status === 'PLATEAU' && (
          <Line
            dataKey="plateau"
            stroke="#9CA3AF"
            strokeDasharray="4 4"
            strokeWidth={1.5}
            dot={false}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  )
}
